import { useEffect } from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot, faDiamondTurnRight, faBus, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import AnalyticsHelper from "../../helpers/analytics"
import './HomePage.less'

// straight-line distance (km) between two lat/long points
const distanceKm = (a, b) => {
    const toRad = d => (d * Math.PI) / 180
    const R = 6371
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.long - a.long)
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
    return R * 2 * Math.asin(Math.sqrt(h))
}

// within this distance of an HKUST gate, assume the user is at HKUST -> "From HKUST"
const HKUST_RADIUS_KM = 1.5
const AUTOROUTE_FLAG = 'eta_autoroute_done'

const HomePage = (props) => {
    const { history, pageProps, getBusStops, getLocations } = props
    const { bus_stops, locations } = pageProps

    useEffect(() => {
        getBusStops({ type: 'hkust' })
        getLocations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Minimal geolocation auto-routing: only once per session, only when data ready.
    useEffect(() => {
        if (sessionStorage.getItem(AUTOROUTE_FLAG)) return
        if (!bus_stops || !locations) return
        if (!('geolocation' in navigator)) return

        sessionStorage.setItem(AUTOROUTE_FLAG, '1')

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const me = { lat: pos.coords.latitude, long: pos.coords.longitude }
                const withCoords = arr => (arr || []).filter(s => s.lat != null && s.long != null)

                const gates = withCoords(bus_stops)
                const locs = withCoords(locations)

                const nearestGate = gates
                    .map(g => ({ s: g, d: distanceKm(me, g) }))
                    .sort((a, b) => a.d - b.d)[0]
                const nearestLoc = locs
                    .map(l => ({ s: l, d: distanceKm(me, l) }))
                    .sort((a, b) => a.d - b.d)[0]

                if (nearestGate && nearestGate.d <= HKUST_RADIUS_KM) {
                    AnalyticsHelper.track('auto_route', { direction: 'from_hkust', slug: nearestGate.s.slug })
                    history.push(`/from-hkust/bus-stops/${nearestGate.s.slug}`)
                } else if (nearestLoc) {
                    AnalyticsHelper.track('auto_route', { direction: 'to_hkust', slug: nearestLoc.s.slug })
                    history.push(`/to-hkust/locations/${nearestLoc.s.slug}`)
                }
            },
            () => {
                /* denied / unavailable: stay on the home page */
                AnalyticsHelper.track('auto_route', { outcome: 'unavailable' })
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bus_stops, locations])

    const handleSectionSelect = (section, path) => {
        AnalyticsHelper.track('select_section', { section })
        history.push(path)
    }

    return (
        <div className="home">
            <div className="home-welcome">
                <h1>Public Transport Live Tracker</h1>
                <p>Get real-time ETA information for buses and minibuses on arrivals and departures to and from the HKUST</p>
            </div>

            <div className="home-actions">
                <Button
                    className="home-btn home-btn-from"
                    onClick={() => handleSectionSelect('from_hkust', '/from-hkust/bus-stops/north-gate')}
                >
                    <FontAwesomeIcon icon={faLocationDot} />
                    <span>From HKUST</span>
                </Button>
                <Button
                    className="home-btn home-btn-to"
                    onClick={() => handleSectionSelect('to_hkust', '/to-hkust/locations')}
                >
                    <FontAwesomeIcon icon={faDiamondTurnRight} />
                    <span>To HKUST</span>
                </Button>
            </div>

            <div className="home-secondary">
                <Button
                    type="text"
                    className="home-shuttle-link"
                    onClick={() => handleSectionSelect('shuttle_bus', '/shuttle-bus/students-and-staff')}
                >
                    <FontAwesomeIcon icon={faBus} className="home-shuttle-link-icon" />
                    <span>Shuttle Bus Information</span>
                    <FontAwesomeIcon icon={faChevronRight} className="home-shuttle-link-chevron" />
                </Button>
            </div>
        </div>
    )
}

export default HomePage
